import { useState, useEffect } from 'react';
import { CONNECTION, NODE_W, NODE_H, NODE_W_ROOT, NODE_H_ROOT } from '../constants';

const QUICK_COLORS = [
  '#93c5fd', '#d20f39', '#FF9B85', '#df8e1d',
  '#40a02b', '#179299', '#1e66f5', '#8839ef', '#ea76cb',
];

const STROKE_WIDTHS = [2, 5];
const STROKE_DASHES = [
  { id: 'solid',  value: '' },
  { id: 'dashed', value: '4,4' },
];

const N = QUICK_COLORS.length;
const R = 7;
const SPACING = 18;
const PAD_X = 10;
const PAD_Y = 5;
const PANEL_W = (N - 1) * SPACING + 2 * R + 2 * PAD_X;
const PANEL_H = 2 * R + 2 * PAD_Y;

// Stroke row geometry — 2 widths + gap + 2 dashes
const DIVIDER_GAP = 6;
const N_STROKE = STROKE_WIDTHS.length + STROKE_DASHES.length; // 4
const STROKE_SPAN = (N_STROKE - 1) * SPACING + DIVIDER_GAP;
const STROKE_PANEL_W = STROKE_SPAN + 2 * R + 2 * PAD_X;
const strokeItemX = (midX, i) =>
  midX - STROKE_SPAN / 2 + i * SPACING + (i >= STROKE_WIDTHS.length ? DIVIDER_GAP : 0);

const calcBezier = (fromNode, toNode) => {
  const dx = toNode.x - fromNode.x;
  const W_from = fromNode.type === 'problem' ? NODE_W_ROOT : NODE_W;
  const W_to = toNode.type === 'problem' ? NODE_W_ROOT : NODE_W;

  const fromPt = dx >= 0
    ? { x: fromNode.x + W_from / 2, y: fromNode.y }
    : { x: fromNode.x - W_from / 2, y: fromNode.y };

  const toPt = dx >= 0
    ? { x: toNode.x - W_to / 2, y: toNode.y }
    : { x: toNode.x + W_to / 2, y: toNode.y };

  const tension = Math.abs(dx) * 0.45;
  const cp1 = { x: fromPt.x + (dx >= 0 ? tension : -tension), y: fromPt.y };
  const cp2 = { x: toPt.x - (dx >= 0 ? tension : -tension), y: toPt.y };

  const d = `M ${fromPt.x} ${fromPt.y} C ${cp1.x} ${cp1.y}, ${cp2.x} ${cp2.y}, ${toPt.x} ${toPt.y}`;
  const midX = (fromPt.x + toPt.x) / 2;
  const midY = (fromPt.y + toPt.y) / 2;

  return { d, midX, midY };
};

export const ConnectionItem = ({ conn, fromNode, toNode, isSelected, onSelect }) => {
  const [animating, setAnimating] = useState(!!conn._animateIn);
  useEffect(() => {
    if (animating) {
      const t = setTimeout(() => setAnimating(false), 700);
      return () => clearTimeout(t);
    }
  }, [animating]);

  const { d, midX, midY } = calcBezier(fromNode, toNode);
  const opacity = fromNode.banned || toNode.banned ? CONNECTION.BANNED_OPACITY : CONNECTION.OPACITY;
  const lineColor = conn.color || CONNECTION.COLOR;
  const displayColor = isSelected ? '#ef4444' : lineColor;
  const strokeW = conn.strokeWidth || CONNECTION.WIDTH;
  const strokeDash = conn.strokeDash || undefined;
  const handleSelect = (e) => {
    e.stopPropagation();
    if (e.pointerType === 'touch') e.preventDefault();
    onSelect(conn);
  };

  return (
    <g onMouseDown={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
      {/* Wide invisible hit area */}
      <path
        d={d}
        stroke="transparent" strokeWidth={16} fill="none"
        style={{ cursor: 'pointer' }}
        onPointerDown={handleSelect}
      />
      <path
        d={d}
        stroke={displayColor}
        strokeWidth={isSelected ? Math.max(strokeW, 2.5) : strokeW}
        strokeDasharray={strokeDash}
        fill="none"
        opacity={opacity}
        style={{
          pointerEvents: 'none',
          ...(animating && { animation: 'drawLine 0.6s ease-out forwards' }),
        }}
      />
      {conn.label && !isSelected && (() => {
        const pillW = Math.max(44, conn.label.length * 6.5 + 28);
        const pH = 20;
        return (
          <g style={{ pointerEvents: 'none' }}>
            {/* Taglia la linea sotto la pill */}
            <rect
              x={midX - pillW / 2 - 5} y={midY - pH / 2 - 3}
              width={pillW + 10} height={pH + 6}
              rx={pH / 2 + 3}
              fill="var(--bg)"
            />
            {/* Pill */}
            <rect
              x={midX - pillW / 2} y={midY - pH / 2}
              width={pillW} height={pH}
              rx={pH / 2}
              fill="var(--bg)"
              stroke={lineColor}
              strokeWidth={1.5}
              style={{ filter: 'drop-shadow(0 1px 4px rgba(0,0,0,0.13))' }}
            />
            <text
              x={midX} y={midY}
              textAnchor="middle" dominantBaseline="central"
              fontSize="10" fill="var(--text-primary)"
              fontWeight="700" letterSpacing="0.04em"
              style={{ userSelect: 'none' }}
            >
              {conn.label}
            </text>
          </g>
        );
      })()}
    </g>
  );
};

export const ConnectionOverlay = ({ conn, fromNode, toNode, onDelete, onColorChange, onStrokeChange, onLabelChange }) => {
  const { midX, midY } = calcBezier(fromNode, toNode);
  const lineColor = conn.color || CONNECTION.COLOR;
  const activeWidth = conn.strokeWidth || CONNECTION.WIDTH;
  const activeDash = conn.strokeDash ?? '';

  const colorY  = midY - 55;
  const strokeY = midY - 28;
  const firstColorX = midX - ((N - 1) / 2) * SPACING;

  // Divider vertical line X: midpoint between last width item and first dash item
  const lastWidthX = strokeItemX(midX, STROKE_WIDTHS.length - 1);
  const firstDashX = strokeItemX(midX, STROKE_WIDTHS.length);
  const dividerX = (lastWidthX + firstDashX) / 2;

  return (
    <g onMouseDown={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>

      {/* ── Row 1: color palette ── */}
      <rect
        x={midX - PANEL_W / 2} y={colorY - R - PAD_Y}
        width={PANEL_W} height={PANEL_H}
        rx={PANEL_H / 2}
        fill="var(--bg)" stroke="var(--border-s)" strokeWidth={1}
      />
      {QUICK_COLORS.map((color, i) => {
        const cx = firstColorX + i * SPACING;
        const isActive = lineColor === color;
        return (
          <g key={color} onClick={(e) => { e.stopPropagation(); onColorChange(conn.id, color); }} style={{ cursor: 'pointer' }}>
            <circle cx={cx} cy={colorY} r={R} fill={color} />
            {isActive && <circle cx={cx} cy={colorY} r={2.5} fill="white" />}
          </g>
        );
      })}

      {/* ── Row 2: stroke width + dash ── */}
      <rect
        x={midX - STROKE_PANEL_W / 2} y={strokeY - R - PAD_Y}
        width={STROKE_PANEL_W} height={PANEL_H}
        rx={PANEL_H / 2}
        fill="var(--bg)" stroke="var(--border-s)" strokeWidth={1}
      />

      {/* Width options */}
      {STROKE_WIDTHS.map((w, i) => {
        const cx = strokeItemX(midX, i);
        const isActive = activeWidth === w;
        return (
          <g key={`w${w}`} onClick={(e) => { e.stopPropagation(); onStrokeChange(conn.id, { strokeWidth: w }); }} style={{ cursor: 'pointer' }}>
            <circle cx={cx} cy={strokeY} r={R} fill={isActive ? 'color-mix(in srgb, var(--accent) 15%, transparent)' : 'var(--surface)'} stroke={isActive ? 'var(--accent)' : 'var(--border-s)'} strokeWidth={isActive ? 1.5 : 1} />
            <line x1={cx - 6} y1={strokeY} x2={cx + 6} y2={strokeY} stroke="var(--text-primary)" strokeWidth={w} strokeLinecap="round" style={{ pointerEvents: 'none' }} />
          </g>
        );
      })}

      {/* Divider */}
      <line x1={dividerX} y1={strokeY - 6} x2={dividerX} y2={strokeY + 6} stroke="var(--border-s)" strokeWidth={1} />

      {/* Dash options */}
      {STROKE_DASHES.map(({ id, value }, i) => {
        const cx = strokeItemX(midX, STROKE_WIDTHS.length + i);
        const isActive = activeDash === value;
        return (
          <g key={id} onClick={(e) => { e.stopPropagation(); onStrokeChange(conn.id, { strokeDash: value }); }} style={{ cursor: 'pointer' }}>
            <circle cx={cx} cy={strokeY} r={R} fill={isActive ? 'color-mix(in srgb, var(--accent) 15%, transparent)' : 'var(--surface)'} stroke={isActive ? 'var(--accent)' : 'var(--border-s)'} strokeWidth={isActive ? 1.5 : 1} />
            <line
              x1={cx - 8} y1={strokeY} x2={cx + 8} y2={strokeY}
              stroke="var(--text-primary)" strokeWidth={1.5} strokeLinecap="butt"
              strokeDasharray={value || undefined}
              style={{ pointerEvents: 'none' }}
            />
          </g>
        );
      })}

      {/* ── Label input ── */}
      {onLabelChange && (
        <foreignObject x={midX - 75} y={midY + 26} width={150} height={26}>
          <input
            type="text"
            value={conn.label || ''}
            onChange={(e) => onLabelChange(conn.id, e.target.value)}
            placeholder="etichetta…"
            style={{
              width: '100%', height: '100%',
              fontSize: '11px', textAlign: 'center',
              border: '1px solid var(--border-s)', borderRadius: '12px',
              padding: '2px 8px', outline: 'none', background: 'var(--bg)',
              boxSizing: 'border-box',
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          />
        </foreignObject>
      )}

      {/* ── Delete button ── */}
      <g
        onClick={(e) => { e.stopPropagation(); onDelete(conn.id); }}
        onPointerDown={(e) => { e.stopPropagation(); if (e.pointerType === 'touch') e.preventDefault(); onDelete(conn.id); }}
        style={{ cursor: 'pointer' }}
      >
        <circle cx={midX} cy={midY} r={10} fill="var(--bg)" stroke="var(--danger)" strokeWidth="2" />
        <text
          x={midX} y={midY + 4}
          textAnchor="middle" fontSize="12" fontWeight="bold" fill="var(--danger)"
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          ×
        </text>
      </g>

    </g>
  );
};
